import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/common/PageHeader';
import FilterCard from '../../components/common/FilterCard';
import transporteurService from '../../services/transporteurService';
import api from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import '../admin/CreateUserAccount.css';
import { useI18n } from '../../i18n/I18nContext';

function Field({ label, id, type = 'text', value, onChange, onBlur, error, required = true, disabled = false }) {
  return (
    <div className="mb-3 w-100">
      <label htmlFor={id} className="form-label fw-medium text-secondary small mb-1">
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        disabled={disabled}
        className={`form-control form-control-lg bg-light border-0 ${error ? 'is-invalid' : ''}`}
        style={{ fontSize: '0.95rem' }}
      />
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
}

const ResponsableTransporteurs = () => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstname: '',
    lastname: '',
    telephone: '',
    enterpriseName: '',
  });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // Prefill entreprise depuis le profil responsable
        const me = await api.get('/users/me').then((r) => r.data).catch(() => null);
        if (me?.enterpriseName) {
          setForm((prev) => ({ ...prev, enterpriseName: me.enterpriseName }));
        }
        const res = await transporteurService.getChildTransporteurs();
        setList(Array.isArray(res) ? res : []);
      } catch (e) {
        toast.error(e?.response?.data?.message || t('responsable.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const errors = useMemo(() => {
    const e = {};
    if (!form.firstname) e.firstname = t('validation.firstNameRequired');
    if (!form.lastname) e.lastname = t('validation.lastNameRequired');
    if (!form.email) e.email = t('validation.emailRequired');
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = t('validation.emailInvalid');
    if (!form.telephone) e.telephone = t('validation.phoneRequired');
    if (!form.enterpriseName) e.enterpriseName = t('validation.enterpriseRequired');
    
    // Password is only required when creating a new account
    if (!editingId) {
      if (!form.password) e.password = t('validation.passwordRequired');
      else if (form.password.length < 6) e.password = t('validation.passwordMin');
      if (!form.confirmPassword) e.confirmPassword = t('validation.confirmPasswordRequired');
      else if (form.confirmPassword !== form.password) e.confirmPassword = t('validation.passwordMatch');
    }
    return e;
  }, [form, t, editingId]);

  const filtered = useMemo(() => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((transporteur) =>
      [transporteur.email, transporteur.firstname, transporteur.lastname, transporteur.enterpriseName, transporteur.telephone]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [list, search]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const hasError = (field) => touched[field] && errors[field];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      email: true,
      password: true,
      firstname: true,
      lastname: true,
      telephone: true,
      enterpriseName: true,
    });
    if (Object.keys(errors).length) {
      toast.warn(t('validation.fixRequired'));
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        email: form.email,
        password: form.password,
        firstname: form.firstname,
        lastname: form.lastname,
        telephone: form.telephone,
        enterpriseName: form.enterpriseName,
        responsableTransporteur: false,
      };

      if (editingId) {
        const updated = await transporteurService.updateChildTransporteur(editingId, payload);
        setList((prev) => prev.map((t) => (t.id === editingId ? updated : t)));
        toast.success(t('responsable.success.updated'));
        setEditingId(null);
      } else {
        const created = await transporteurService.createChildTransporteur(payload);
        setList((prev) => [created, ...prev]);
        toast.success(t('responsable.success.created'));
      }

      setForm({
        email: '',
        password: '',
        confirmPassword: '',
        firstname: '',
        lastname: '',
        telephone: '',
        enterpriseName: form.enterpriseName,
      });
      setTouched({});
    } catch (e) {
      const errKey = editingId ? 'responsable.errors.updateFailed' : 'responsable.errors.createFailed';
      toast.error(e?.response?.data?.message || t(errKey));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (transporteur) => {
    setEditingId(transporteur.id);
    setForm({
      email: transporteur.email || '',
      password: '',
      confirmPassword: '',
      firstname: transporteur.firstname || '',
      lastname: transporteur.lastname || '',
      telephone: transporteur.telephone || '',
      enterpriseName: transporteur.enterpriseName || '',
    });
    setTouched({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setForm({
      email: '',
      password: '',
      confirmPassword: '',
      firstname: '',
      lastname: '',
      telephone: '',
      enterpriseName: list.length > 0 ? list[0].enterpriseName : '',
    });
    setTouched({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('responsable.confirmDelete'))) return;
    try {
      await transporteurService.deleteChildTransporteur(id);
      setList((prev) => prev.filter((t) => t.id !== id));
      toast.success(t('responsable.success.deleted'));
      if (editingId === id) handleCancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('responsable.errors.deleteFailed'));
    }
  };

  return (
    <DashboardLayout>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="container-fluid">
        <PageHeader
          title={t('responsable.title')}
          subtitle={t('responsable.subtitle')}
        />

        <div className="row g-4">
          <div className="col-lg-5">
            <div className="create-account-container">
              <div className="create-account-card" style={{ maxWidth: '53.75rem', position: 'sticky', top: '100px' }}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h2 className="create-account-title mb-0">
                    {editingId ? t('responsable.editAccount') : t('responsable.createTitle')}
                  </h2>
                  {editingId && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleCancelEdit}>
                      {t('responsable.cancel')}
                    </button>
                  )}
                </div>
                <p className="create-account-subtitle text-muted mb-4">
                  {editingId ? t('responsable.editSubtitle') : t('responsable.typeFixed')}
                </p>
                <form onSubmit={handleSubmit}>
                  <div className="two-column">
                    <Field
                      label={t('common.firstName')}
                      id="firstName"
                      value={form.firstname}
                      onChange={(e) => handleChange('firstname', e.target.value)}
                      onBlur={handleBlur('firstname')}
                      error={hasError('firstname') && errors.firstname}
                    />
                    <Field
                      label={t('common.lastName')}
                      id="lastName"
                      value={form.lastname}
                      onChange={(e) => handleChange('lastname', e.target.value)}
                      onBlur={handleBlur('lastname')}
                      error={hasError('lastname') && errors.lastname}
                    />
                  </div>

                  <div className="two-column">
                    <Field
                      label={t('common.email')}
                      id="email"
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      onBlur={handleBlur('email')}
                      error={hasError('email') && errors.email}
                    />
                    <Field
                      label={t('common.phone')}
                      id="telephone"
                      value={form.telephone}
                      onChange={(e) => handleChange('telephone', e.target.value)}
                      onBlur={handleBlur('telephone')}
                      error={hasError('telephone') && errors.telephone}
                    />
                  </div>

                  <Field
                    label={t('common.enterpriseName')}
                    id="enterpriseName"
                    value={form.enterpriseName}
                    onChange={(e) => handleChange('enterpriseName', e.target.value)}
                    onBlur={handleBlur('enterpriseName')}
                    error={hasError('enterpriseName') && errors.enterpriseName}
                  />

                  {!editingId && (
                    <div className="two-column">
                      <Field
                        label={t('common.password')}
                        id="password"
                        type="password"
                        value={form.password}
                        onChange={(e) => handleChange('password', e.target.value)}
                        onBlur={handleBlur('password')}
                        error={hasError('password') && errors.password}
                      />
                      <Field
                        label={t('common.confirmPassword')}
                        id="confirmPassword"
                        type="password"
                        value={form.confirmPassword}
                        onChange={(e) => handleChange('confirmPassword', e.target.value)}
                        onBlur={handleBlur('confirmPassword')}
                        error={hasError('confirmPassword') && errors.confirmPassword}
                      />
                    </div>
                  )}

                  <div className="two-column">
                    <div className="mb-3 w-100">
                      <label className="form-label fw-medium text-secondary small mb-1">{t('responsable.accountType')}</label>
                      <input className="form-control form-control-lg bg-light border-0 text-muted" value={t('common.exploitant')} disabled style={{ fontSize: '0.95rem' }} />
                    </div>
                    <div />
                  </div>

                  <div className="mt-4">
                    <button 
                      type="submit" 
                      className="btn btn-primary w-100 py-3 fw-bold rounded-3 shadow-sm d-flex justify-content-center align-items-center gap-2" 
                      style={{ fontSize: '1.05rem', transition: 'all 0.2s' }}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> {t('common.saving')}</>
                      ) : (
                        editingId ? <><i className="bi bi-check-circle"></i> {t('common.save')}</> : <><i className="bi bi-person-plus-fill"></i> {t('responsable.createAccount')}</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-7">
            <FilterCard>
              <div className="row g-3 align-items-center">
                <div className="col-md-8">
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-search"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder={t('responsable.searchPlaceholder')}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4 text-end text-muted">
                  {t('responsable.count', { count: filtered.length })}
                </div>
              </div>
            </FilterCard>

            <div className="row g-3 mt-2">
              {loading ? (
                <div className="text-center py-5 text-muted">{t('common.loading')}</div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  {t('responsable.noTransporteurs')}
                </div>
              ) : (
                filtered.map((transporteur) => (
                  <div className="col-md-6" key={transporteur.id}>
                    <div className="card shadow-sm h-100 border-0" style={{ borderRadius: '12px', transition: 'all 0.2s', border: editingId === transporteur.id ? '2px solid #0056b3' : '1px solid #e2e8f0' }}>
                      <div className="card-body p-4">
                        <div className="d-flex align-items-start justify-content-between mb-3">
                          <div className="d-flex align-items-center gap-3">
                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center text-primary fw-bold" style={{ width: '48px', height: '48px', fontSize: '1.2rem' }}>
                              {transporteur.firstname?.charAt(0)}{transporteur.lastname?.charAt(0)}
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold">{transporteur.firstname} {transporteur.lastname}</h6>
                              <span className="badge bg-primary bg-opacity-10 text-primary mt-1" style={{ fontSize: '0.75rem' }}>
                                {t('common.exploitant')}
                              </span>
                            </div>
                          </div>
                          
                          <div className="dropdown">
                            <button className="btn btn-link text-muted p-0" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                              <i className="bi bi-three-dots-vertical"></i>
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end shadow-sm border-0">
                              <li>
                                <button className="dropdown-item d-flex align-items-center gap-2" onClick={() => handleEdit(transporteur)}>
                                  <i className="bi bi-pencil text-primary"></i>
                                  {t('common.edit')}
                                </button>
                              </li>
                              <li><hr className="dropdown-divider" /></li>
                              <li>
                                <button className="dropdown-item d-flex align-items-center gap-2 text-danger" onClick={() => handleDelete(transporteur.id)}>
                                  <i className="bi bi-trash"></i>
                                  {t('common.delete')}
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>

                        <div className="text-muted small d-flex flex-column gap-2 mt-3 p-3 bg-light rounded-3">
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-envelope text-secondary"></i>
                            <span className="text-truncate">{transporteur.email}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-telephone text-secondary"></i>
                            <span>{transporteur.telephone || t('common.nc')}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-building text-secondary"></i>
                            <span className="text-truncate">{transporteur.enterpriseName}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ResponsableTransporteurs;

